import { useState } from 'react';

import { formatDate } from 'date-fns';
import { Formik } from 'formik';
import { useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';

import { Box, Button, Card, Chip, Divider, Stack, styled, Typography } from '@mui/material';

import { ConfirmDialog } from '../../../components/dialogs';
import { TextField } from '../../../components/inputs';
import { Modal } from '../../../components/Modal';
import { useApprovePostRequestOverride } from '../../../lib/post/hooks/useApprovePostRequestOverride';
import { useGetPostRequestOverride } from '../../../lib/post/hooks/useGetPostRequest';
import { useRejectPostRequestOverride } from '../../../lib/post/hooks/useRejectPostRequestOverride';
import { PostRequestStatus } from '../../../types/PostRequest';

type RejectPostRequestOverrideFormType = {
  rejectedReason: string;
};

const RejectPostRequestOverrideSchema = Yup.object().shape({
  rejectedReason: Yup.string().required('Rejection reason is required'),
});

function PostRequestOverrideDetailView() {
  const navigate = useNavigate();
  const { id } = useParams();
  const postRequestId = id ?? '';
  const [rejectedReasonModalIsOpen, setRejectedReasonModalIsOpen] = useState(false);

  const { data: postRequest, isLoading: isLoadingPostRequest } = useGetPostRequestOverride(postRequestId);

  const { mutate: approvePostRequestOverride, isLoading: isApproving } = useApprovePostRequestOverride();
  const { mutate: rejectPostRequestOverride, isLoading: isRejecting } = useRejectPostRequestOverride();
  const initialValues: RejectPostRequestOverrideFormType = { rejectedReason: '' };

  if (isLoadingPostRequest) {
    return <div>Loading...</div>;
  }

  return (
    <Container>
      <TopBar>
        <Typography variant="h5">Post Request Override Details</Typography>
      </TopBar>

      <StyledCard>
        <Stack width="100%" padding="20px" spacing={3}>
          <Box width="100%">
            <Typography fontWeight={'bold'} gutterBottom>
              Requested Article Title:
            </Typography>
            <Typography gutterBottom>{postRequest?.post.title}</Typography>
          </Box>
          <Box width="100%">
            <Typography fontWeight={'bold'} gutterBottom>
              Requested by:
            </Typography>
            <Typography gutterBottom>{postRequest?.requester.username}</Typography>
          </Box>
          <Box width="100%">
            <Typography fontWeight={'bold'} gutterBottom>
              Date Submitted:
            </Typography>
            <Typography gutterBottom>{formatDate(postRequest?.created ?? new Date(), 'MMM dd, yyyy hh:mm a')}</Typography>
          </Box>

          <Box width="100%">
            <Typography fontWeight={'bold'} gutterBottom>
              Status:
            </Typography>
            {postRequest?.status === PostRequestStatus.APPROVED ? (
              <Chip label="Approved" color="success" variant="outlined" />
            ) : postRequest?.status === PostRequestStatus.REJECTED ? (
              <Chip label="Rejected" color="error" variant="outlined" />
            ) : (
              <Chip label="Pending" color="warning" variant="outlined" />
            )}
          </Box>

          <Box width="100%">
            <Typography fontWeight={'bold'} gutterBottom>
              Request Type:
            </Typography>
            {postRequest?.type ? (
              postRequest.type === 'edit' ? (
                <Chip label="Edit" color="info" size="small" variant="outlined" />
              ) : (
                <Chip label="Delete" color="error" size="small" variant="outlined" />
              )
            ) : (
              <Typography>—</Typography>
            )}
          </Box>

          {postRequest?.status === PostRequestStatus.REJECTED && (
            <Box width="100%">
              <Typography fontWeight={'bold'} gutterBottom>
                Rejected Reason:
              </Typography>
              <Typography gutterBottom>{postRequest?.rejectedReason}</Typography>
            </Box>
          )}

          <Box width="100%">
            <Typography fontWeight={'bold'} gutterBottom>
              Reason:
            </Typography>
            <Typography gutterBottom>{postRequest?.reason}</Typography>
          </Box>

          <Box width="100%">
            <Typography fontWeight={'bold'} gutterBottom>
              Description:
            </Typography>
            <Typography gutterBottom>{postRequest?.description}</Typography>
          </Box>

          <Box width="100%">
            <Divider />
          </Box>

          <Stack flexDirection="row" gap={2} width="100%" justifyContent="space-between">
            <Button onClick={() => navigate(-1)} variant="outlined">
              Cancel
            </Button>
            <Stack flexDirection="row" gap={2}>
              <ConfirmDialog
                trigger={(open) => (
                  <Button
                    disabled={postRequest?.status !== PostRequestStatus.PENDING}
                    onClick={open}
                    type="button"
                    variant="outlined"
                    color="success"
                  >
                    Approve
                  </Button>
                )}
                title="Approve post edit request"
                description="Are you sure you want to approve this post edit request?"
                cancelBtnText={'Cancel'}
                submitBtnText={'Approve'}
                onConfirm={async (close) => {
                  await approvePostRequestOverride(
                    { requestId: postRequest?.id || '' },
                    {
                      onSuccess: () => {
                        navigate(-1);
                      },
                    },
                  );
                  close();
                }}
                disabled={isApproving}
                loading={isApproving}
              />
              <ConfirmDialog
                trigger={(open) => (
                  <Button
                    onClick={open}
                    type="button"
                    variant="outlined"
                    color="error"
                    disabled={postRequest?.status !== PostRequestStatus.PENDING}
                  >
                    Reject
                  </Button>
                )}
                title="Reject post request override"
                description="Are you sure you want to reject this post request override?"
                cancelBtnText={'Cancel'}
                submitBtnText={'Submit'}
                onConfirm={async (close) => {
                  setRejectedReasonModalIsOpen(true);
                  close();
                }}
                disabled={isRejecting}
                loading={isRejecting}
              />
            </Stack>
          </Stack>
        </Stack>
      </StyledCard>

      <Modal
        uniqueId="add-rejected-reason"
        isOpen={rejectedReasonModalIsOpen}
        handleClose={() => {
          setRejectedReasonModalIsOpen(false);
        }}
      >
        <Formik
          initialValues={initialValues}
          validationSchema={RejectPostRequestOverrideSchema}
          validateOnBlur
          onSubmit={(values) => {
            rejectPostRequestOverride(
              { requestId: postRequest?.id || '', dto: { rejectedReason: values.rejectedReason } },
              {
                onSuccess: () => {
                  navigate(-1);
                },
              },
            );
            setRejectedReasonModalIsOpen(false);
          }}
        >
          {({ values, errors, touched, handleChange, handleBlur, handleSubmit }) => (
            <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '300px' }}>
              <Typography variant="h6">Reject Post Request Override</Typography>
              <TextField
                label="Rejection Reason"
                name="rejectedReason"
                multiline
                minRows={4}
                value={values.rejectedReason}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.rejectedReason && Boolean(errors.rejectedReason)}
                helperText={touched.rejectedReason && errors.rejectedReason}
                fullWidth
              />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                <Button type="button" variant="outlined" onClick={() => setRejectedReasonModalIsOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" variant="contained" color="primary" disabled={isRejecting}>
                  Submit
                </Button>
              </Box>
            </Box>
          )}
        </Formik>
      </Modal>
    </Container>
  );
}

const Container = styled('div')({
  padding: '20px',
  backgroundColor: '#eee',
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
});

const TopBar = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  margin: '8px 0',
  justifyContent: 'space-between',
});

const StyledCard = styled(Card)({
  display: 'flex',
});

export default PostRequestOverrideDetailView;
